import { Fetch } from "@syncfusion/ej2-base";
import {
  CrudOptions,
  DataOptions,
  DataResult,
  Query,
  Predicate,
} from "@syncfusion/ej2-data";
import { GridColumn } from "@syncfusion/ej2-react-grids";
import _ from "lodash";
import moment from "moment-timezone";
import {
  convertDateTo_EST_timeZone_AsDate,
  removeOffSetFromDate,
} from "../../../utils/momentTimeZone";
import {
  ODataV4QueryStringAndMaybeHeadersFactory,
  ODataV4TransformDataMethod,
} from "../../../stores/OData/types";
import BaseODataV4Adaptor from "../../../stores/OData/BaseODataV4Adaptor";

export default class GridODataV4Adaptor extends BaseODataV4Adaptor<any> {
  constructor(
    private columnsConfiguration?: GridColumn[],
    beforeSendFn?: ODataV4QueryStringAndMaybeHeadersFactory,
    transformData?: ODataV4TransformDataMethod,
    removeLeadingQuestionMark: boolean = false,
    replaceUndefinedInQueryStringWithNulls: boolean = true,
    removeUnsupportedParameters: boolean = false
  ) {
    super({
      beforeSendFn,
      transformData,
      removeLeadingQuestionMark,
      replaceUndefinedInQueryStringWithNulls,
      removeUnsupportedParameters,
    });
  }

  onPredicate(
    predicate: Predicate,
    query: Query | boolean,
    requiresCast?: boolean
  ): string {
    // "date" types will display as is without time conversion
    // Type on Column Configuration should be date, ideally with format: { format: "MM/dd/yyyy", type: "date" }
    const dateColumnConfigs = this.columnsConfiguration?.filter(
      (x) => x.type === "date"
    );

    if (dateColumnConfigs?.map((x) => x.field).includes(predicate.field)) {
      if (predicate.value) {
        const momentDate = moment(predicate.value as Date);
        if (momentDate.isValid()) {
          const userTimezoneOffset =
            momentDate.toDate().getTimezoneOffset() * 60000;
          const newDateNoTz = new Date(
            momentDate.toDate().getTime() - userTimezoneOffset
          );
          const convertedEST = convertDateTo_EST_timeZone_AsDate(newDateNoTz);
          predicate.value = convertedEST;
        }
      }
    }

    const dateTimeColumnConfigs = this.columnsConfiguration?.filter(
      (x) => x.type === "datetime"
    );

    if (dateTimeColumnConfigs?.map((x) => x.field).includes(predicate.field)) {
      // Exclude search text from being parsed as a date
      if (predicate.value && typeof predicate.value === "object") {
        const momentDate = moment(predicate.value as Date);
        if (momentDate.isValid()) {
          const cleanedDate = removeOffSetFromDate(momentDate.toString());
          const convertedEST = convertDateTo_EST_timeZone_AsDate(cleanedDate);
          predicate.value = convertedEST;
        }
      }
    }

    return super.onPredicate(predicate, query, requiresCast);
  }

  processResponse(
    data: DataResult,
    ds?: DataOptions,
    query?: Query,
    xhr?: Request,
    request?: Fetch,
    changes?: CrudOptions
  ): Object {
    const dateColumnReults = this.processDateColumns(data);

    if (!!dateColumnReults) {
      data = dateColumnReults;
    }

    const datetimeColumnResults = this.processDateTimeColumns(data);
    if (!!datetimeColumnResults) {
      data = datetimeColumnResults;
    }

    const processResult = super.processResponse.apply(this, [
      data,
      ds,
      query,
      xhr,
      request,
      changes,
    ]);

    return processResult;
  }

  processDateTimeColumns(data: DataResult): DataResult | undefined {
    const dateTimeColumnConfigs = this.columnsConfiguration?.filter(
      (x) => x.type === "datetime"
    );
    if (dateTimeColumnConfigs) {
      const cleaned = (_.get(data, "value") as any as Object[])?.map(
        (resultData) => {
          dateTimeColumnConfigs.forEach((columnConfig) => {
            const rawDate = _.get(resultData, columnConfig.field) as string;
            if (rawDate) {
              const momentDate = moment(rawDate);
              if (momentDate.isValid()) {
                const cleanedDate = removeOffSetFromDate(rawDate);
                const convertedEST =
                  convertDateTo_EST_timeZone_AsDate(cleanedDate);

                _.set(resultData, columnConfig.field, convertedEST);
              }
            }
          });

          return resultData;
        }
      );

      return { ...data, ...{ value: cleaned } };
    }
  }

  processDateColumns(data: DataResult): DataResult | undefined {
    // "date" types will display as is without time conversion
    // Type on Column Configuration should be date, ideally with format: { format: "MM/dd/yyyy", type: "date" }
    const dateColumnConfigs = this.columnsConfiguration?.filter(
      (x) => x.type === "date"
    );
    if (dateColumnConfigs) {
      const cleaned = (_.get(data, "value") as any as Object[])?.map(
        (resultData) => {
          dateColumnConfigs.forEach((columnConfig) => {
            let rawDate = _.get(resultData, columnConfig.field);

            if (rawDate instanceof Date) {
              rawDate = rawDate.toISOString();
            }
            // since we now officially set the odata timestamp to UTC, we can ensure that Z will always be returned
            const rawDateWithoutZone = rawDate?.replace("Z", "");
            if (rawDateWithoutZone) {
              _.set(
                resultData,
                columnConfig.field,
                new Date(rawDateWithoutZone)
              );
            } else {
              _.set(resultData, columnConfig.field, null /* blanks */);
            }
          });

          return resultData;
        }
      );

      return { ...data, ...{ value: cleaned } };
    }
  }
}
